import { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { ApiError, troubleshoot } from "./api";
import type { TroubleshootIssue, TroubleshootResult, TroubleshootSeverity } from "./types";
import { colors } from "./theme";

// Client-side cap: only the tail matters for triage, and it keeps the request
// body well under the API gateway limit. The server truncates too.
const MAX_CHARS = 200 * 1024;

// Paste or upload an agent's helper-YYYYMMDD.log → Bedrock returns a structured
// diagnosis. Supervisors/admins use this when an agent reports the recorder
// isn't working; the log lives at
// %LOCALAPPDATA%\uccx-recorder-helper\logs\ on the agent's machine.
export default function TroubleshootPage() {
  const [logText, setLogText] = useState("");
  const [result, setResult] = useState<TroubleshootResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncatedNote, setTruncatedNote] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const full = String(reader.result ?? "");
      if (full.length > MAX_CHARS) {
        setLogText(full.slice(full.length - MAX_CHARS));
        setTruncatedNote(`Loaded the last ${Math.round(MAX_CHARS / 1024)} KB of ${file.name} (${Math.round(full.length / 1024)} KB total).`);
      } else {
        setLogText(full);
        setTruncatedNote(null);
      }
    };
    reader.readAsText(file);
    // Allow re-selecting the same file later.
    e.target.value = "";
  }

  async function analyze() {
    const t = logText.trim();
    if (!t) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await troubleshoot(t));
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  function clearAll() {
    setLogText("");
    setResult(null);
    setError(null);
    setTruncatedNote(null);
  }

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body2" sx={{ color: colors.gray600, mb: 2 }}>
            Paste or upload the agent's helper log
            (<code>%LOCALAPPDATA%\uccx-recorder-helper\logs\helper-YYYYMMDD.log</code>).
            We'll run it through an AI triage and return likely causes + fixes.
          </Typography>

          <TextField
            multiline
            minRows={10}
            maxRows={24}
            fullWidth
            placeholder="Paste log contents here…"
            value={logText}
            onChange={(e) => setLogText(e.target.value)}
            sx={{ "& textarea": { fontFamily: "monospace", fontSize: "0.78rem" } }}
          />
          {truncatedNote && (
            <Typography variant="caption" sx={{ color: colors.gray500, display: "block", mt: 0.5 }}>
              {truncatedNote}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center", flexWrap: "wrap" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".log,.txt,text/plain"
              hidden
              onChange={onPickFile}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon fontSize="small" />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload log file
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            {logText && (
              <Button size="small" onClick={clearAll} disabled={loading}>
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              onClick={analyze}
              disabled={loading || logText.trim().length === 0}
            >
              {loading ? "Analyzing…" : "Analyze"}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && <ResultPanel result={result} />}
    </Box>
  );
}

function ResultPanel({ result }: { result: TroubleshootResult }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Diagnosis
          </Typography>
          <Chip label={result.model} size="small" variant="outlined" sx={{ fontFamily: "monospace" }} />
        </Stack>
        <Typography variant="body1" sx={{ mb: 2 }}>{result.summary}</Typography>

        {result.issues.length === 0 && !result.rawText && (
          <Typography variant="body2" sx={{ color: colors.gray500, fontStyle: "italic" }}>
            No specific issues flagged.
          </Typography>
        )}

        <Stack spacing={1.5}>
          {result.issues.map((issue, i) => (
            <IssueCard key={i} issue={issue} />
          ))}
        </Stack>

        {result.rawText && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: colors.gray500, display: "block", mb: 0.5 }}>
              The model didn't return structured output — raw response:
            </Typography>
            <Box
              component="pre"
              sx={{
                p: 1.5,
                m: 0,
                background: colors.gray50,
                border: `1px solid ${colors.gray200}`,
                borderRadius: 1,
                fontSize: "0.78rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {result.rawText}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function IssueCard({ issue }: { issue: TroubleshootIssue }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 1,
        border: `1px solid ${colors.gray200}`,
        borderLeft: `4px solid ${severityColor(issue.severity)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Chip
          label={issue.severity}
          size="small"
          sx={{
            bgcolor: severityColor(issue.severity),
            color: "#fff",
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.65rem",
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{issue.title}</Typography>
      </Stack>
      {issue.detail && (
        <Typography variant="body2" sx={{ color: colors.gray700, mb: issue.fix ? 0.75 : 0 }}>
          {issue.detail}
        </Typography>
      )}
      {issue.fix && (
        <Typography variant="body2" sx={{ color: colors.gray900 }}>
          <strong>Fix:</strong> {issue.fix}
        </Typography>
      )}
    </Box>
  );
}

function severityColor(s: TroubleshootSeverity): string {
  if (s === "critical") return colors.error;
  if (s === "warning") return colors.warning;
  return colors.primary;
}
